import { type CurrencyName } from '@webapp/core/entities/external/Currency'
import {
  GlobalSearchResultTypes,
  type GlobalSearchResultTypeEnum,
} from '../../value-objects/enums'
import { type GlobalSearchItemMetadata } from '../../value-objects/GlobalSearchItemMetadata'
import { type GlobalSearchResultConstructor } from '../../value-objects/GlobalSearchResult'
import { type GlobalSearchResultItemConstructor } from '../../value-objects/GlobalSearchResultItem'
import { type GlobalSearchResultApiModel } from '../api-models/GlobalSearchResultApiModel'
import {
  GlobalSearchEnvelopeComponentTypes,
  GlobalSearchWarningCodes,
  type GlobalSearchResponsePayload,
} from '../types/GlobalSearchResponse'

function mapAmount(rawAmount?: {
  value: number
  currency_name: CurrencyName
}): { value: number; currencyName: CurrencyName } | undefined {
  return rawAmount
    ? { value: rawAmount.value, currencyName: rawAmount.currency_name }
    : undefined
}

function mapItemMetadata(
  item: GlobalSearchResultApiModel
): GlobalSearchItemMetadata {
  const identifiers = item.attributes.identifiers ?? {}

  switch (item.type) {
    case GlobalSearchResultTypes.BILL:
      return {
        status: item.attributes.status,
        date: item.attributes.date ?? null,
        invoiceNumber: identifiers.invoice_number ?? null,
        amount: mapAmount(item.attributes.amount),
      }
    case GlobalSearchResultTypes.PAYMENT:
      return {
        status: item.attributes.status,
        date: item.attributes.date ?? null,
        vendorPaymentMethod: identifiers.vendor_payment_method ?? null,
        amount: mapAmount(item.attributes.amount),
      }
    case GlobalSearchResultTypes.CATALOG_ITEM:
      return {
        sku: identifiers.sku ?? null,
        vendorName: item.attributes.vendor_name ?? null,
        amount: mapAmount(item.attributes.amount),
      }
    case GlobalSearchResultTypes.VENDOR:
      return {
        isPreferredVendor: item.attributes.is_preferred,
        externalId: identifiers.vendor_external_id ?? null,
      }
    case GlobalSearchResultTypes.CONTRACT:
      return {
        status: item.attributes.status,
        endDate: item.attributes.end_date ?? null,
        vendorName: item.attributes.vendor_name ?? null,
        ownerName: item.attributes.owner_name ?? null,
      }
    case GlobalSearchResultTypes.ORDER_REQUEST:
    case GlobalSearchResultTypes.EXPENSE_REQUEST:
    case GlobalSearchResultTypes.TRAVEL_REQUEST:
    case GlobalSearchResultTypes.PURCHASE_ORDER:
      return {
        status: item.attributes.status,
        date: item.attributes.date ?? null,
        amount: mapAmount(item.attributes.amount),
      }
  }
}

export function mapGlobalSearchResultsApiModel(
  payload: GlobalSearchResponsePayload
): GlobalSearchResultConstructor {
  const { data: items, metadata, envelope, conversation_id } = payload

  const mappedItems: GlobalSearchResultItemConstructor[] = items.map((item) => {
    // For order-request, `order_number` is the user-facing identifier.
    // For every other type, the displayed `id` is `subject.id`.
    return {
      type: item.type,
      uuid: item.uuid,
      id:
        item.type === GlobalSearchResultTypes.ORDER_REQUEST
          ? String(item.attributes.order_number ?? item.attributes.subject.id)
          : item.attributes.subject.id,
      label: item.attributes.subject.label,
      metadata: mapItemMetadata(item),
    }
  })

  const counts = new Map<GlobalSearchResultTypeEnum, number>()
  if (metadata?.counts) {
    for (const entry of metadata.counts) {
      counts.set(entry.type, entry.count)
    }
  }

  const warnings = metadata?.warnings ?? []
  const isTruncated = warnings.some(
    (w) => w.code === GlobalSearchWarningCodes.TRUNCATED_RESULTS
  )

  const noResultsWarning = warnings.find(
    (w) => w.code === GlobalSearchWarningCodes.NO_RESULTS
  )
  const unsupportedQueryWarning = warnings.find(
    (w) => w.code === GlobalSearchWarningCodes.UNSUPPORTED_QUERY
  )
  const isUnsupportedQuery = unsupportedQueryWarning !== undefined

  const noResultsMessage = noResultsWarning?.message ?? null
  const unsupportedQueryMessage = unsupportedQueryWarning?.message ?? null

  const total = metadata?.total ?? 0

  return {
    items: mappedItems,
    counts,
    total,
    isTruncated,
    isUnsupportedQuery,
    noResultsMessage,
    unsupportedQueryMessage,
    showConversationConfirmation:
      envelope?.components.some(
        (component) =>
          component.type ===
          GlobalSearchEnvelopeComponentTypes.CONFIRMATION_BANNER
      ) ?? false,
    originalQuery: metadata?.original_query ?? null,
    conversationId: conversation_id ?? null,
  }
}
